import { Card, CardContent, CardHeader, Divider, Grid, Stack, styled, Tooltip, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import CurrentYearPerfromanceGraph from "./CurrentYearPerformanceTile/CurrentYearPerfromanceGraph";
import LastFiveYearsPerfromanceGraph from "./FiveYearPerformanceTile/LastFiveYearsPerfromanceGraph";
import TargetFilter from "./CurrentYearPerformanceTile/TargetFilter";
import { useMemo, useState } from "react";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { IMG_NO_RESULTS_OR_MATCHES } from "~/utils/url/staticResources";
import { findFirstNonZeroGap, transformedCustomerPerformanceDashboardData } from "./CPDUtils";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { formatCurrency } from "~/utils/currencyFormatter";
import { CURRENCY_ISO, FALLBACK_DATE_FORMAT } from "~/utils/constants";
import dayjs from "dayjs";
import type { FilterOptionDTO, PerformanceGapsDataDTO, YearlyDataDTO } from "./CPDTypes";
import { DateFormatType, getPattern } from "~/utils/DateFormatter";
import BonusAgreementTile from "./components/BonusAgreementTile/BonusAgreementTile";

export default function CPD() {
  const { t } = useTranslation();

  const customerPerformanceDashboardData = useMemo(() => transformedCustomerPerformanceDashboardData(t), [t]);
  const bonusAgreementData = useMemo(
    () =>
      transformBonusAgreementData(
        window.SCHA.user.customerPerformanceGraphsData.customerPerformanceBonusAgreement,
        window.SCHA.user.customerPerformanceGraphsData.bonusGraphView,
      ),
    [],
  );
  const initialSelection = findFirstNonZeroGap(customerPerformanceDashboardData.performanceGapsData);

  const [selectedTarget, setSelectedTarget] = useState<FilterOptionDTO | undefined>(initialSelection?.value);
  const [selectedTargetIndex, setSelectedTargetIndex] = useState<number>(initialSelection?.index ?? 0);

  const isDesktop = useIsMinDesktopSize();
  const currentYear = new Date().getFullYear();

  const handleClick = (target: FilterOptionDTO, index: number) => {
    setSelectedTarget(target);
    setSelectedTargetIndex(index);
  };

  let currentYearCPDGraphData: PerformanceGapsDataDTO = customerPerformanceDashboardData?.performanceGapsData;
  const lastFiveYearCPDGraphData: YearlyDataDTO = customerPerformanceDashboardData?.yearly_data;

  const y_target = selectedTarget?.yearlyTarget ?? 0;
  const y_gap = selectedTarget?.gapYearlyTarget ?? 0;

  currentYearCPDGraphData = {
    ...currentYearCPDGraphData,
    target: [...currentYearCPDGraphData.target, y_target],
    gap: [...currentYearCPDGraphData.gap, y_gap],
  };

  const noData = () => (
    <NoDataCard>
      <Typography variant="titleMedium">{t("cpd.graph.noData")}</Typography>
      <NodataImage src={IMG_NO_RESULTS_OR_MATCHES} alt={t("cpd.graph.noData")} />
    </NoDataCard>
  );

  const CY_processedChartData = {
    invoice: currentYearCPDGraphData.invoice,
    orderbook: currentYearCPDGraphData.orderbook,
    gap: currentYearCPDGraphData.gap,
    target: currentYearCPDGraphData.target,
  };

  const LFY_processedInvoice: (number | null)[] = lastFiveYearCPDGraphData?.invoice;
  const LFY_processedOrderbook: (number | null)[] = lastFiveYearCPDGraphData?.orderbook;

  const isCurrentYearCPDGraphDataEmpty =
    currentYearCPDGraphData.invoice.length == 0 && currentYearCPDGraphData.orderbook.length == 0;
  const isLastFiveYearCPDGraphDataEmpty =
    lastFiveYearCPDGraphData.invoice.length == 0 && lastFiveYearCPDGraphData.orderbook.length == 0;

  const currentYearCardHeaderTitle = (
    <Stack direction={"row"} alignItems={"center"} gap={1}>
      <Typography variant="titleLarge">
        {t("cpd.graph1.title", {
          year: currentYear,
        })}
      </Typography>
      <Tooltip
        variant="rich"
        placement="top"
        arrow={true}
        title={
          <Stack gap={0.5}>
            <Typography>{t("cpd.graph1.infoTooltip.title")}</Typography>
            {currentYearCPDGraphData?.filterOptions?.map((target, index) => (
              <Typography key={index}>
                {parse(
                  t("cpd.graph1.infoTooltip.data", {
                    index: index + 1,
                    target: formatCurrency(parseInt(target.value), CURRENCY_ISO),
                  }),
                )}
              </Typography>
            ))}
          </Stack>
        }
      >
        <InfoOutlineIcon fontSize="medium" />
      </Tooltip>
    </Stack>
  );

  const slotProps = {
    title: { variant: "titleLarge" },
    subheader: { variant: "bodySmall", color: "text.secondary" },
    action: { sx: { alignSelf: "center" } },
  };
  return (
    <>
      <StyledGrid size={isDesktop ? 4 : 12}>
        {isCurrentYearCPDGraphDataEmpty ? (
          noData()
        ) : (
          <StyledCard>
            <SpacedCardHeader
              title={currentYearCardHeaderTitle}
              subheader={t("cpd.graph.lastUpdated", {
                date: getLastUpdatedString(currentYearCPDGraphData.lastUpdatedDate),
              })}
              slotProps={slotProps}
              action={
                currentYearCPDGraphData?.filterOptions.length > 0 && (
                  <TargetFilter
                    selectedTarget={selectedTarget}
                    targets={currentYearCPDGraphData?.filterOptions}
                    handleClick={handleClick}
                    selectedTargetIndex={selectedTargetIndex}
                  />
                )
              }
            />
            <Divider />
            <CardContent>
              {currentYearCPDGraphData && (
                <CurrentYearPerfromanceGraph
                  chartData={CY_processedChartData}
                  currencyCode={currentYearCPDGraphData.currency || CURRENCY_ISO}
                />
              )}
            </CardContent>
          </StyledCard>
        )}
      </StyledGrid>
      <StyledGrid size={isDesktop ? 4 : 12}>
        {isLastFiveYearCPDGraphDataEmpty ? (
          noData()
        ) : (
          <StyledCard>
            <SpacedCardHeader
              title={t("cpd.graph2.title")}
              subheader={t("cpd.graph.lastUpdated", {
                date: getLastUpdatedString(lastFiveYearCPDGraphData.lastUpdatedDate),
              })}
              slotProps={slotProps}
            />
            <Divider />
            <CardContent>
              <LastFiveYearsPerfromanceGraph
                invoice={LFY_processedInvoice}
                orderbook={LFY_processedOrderbook}
                currencyCode={lastFiveYearCPDGraphData.currency || CURRENCY_ISO}
              />
            </CardContent>
          </StyledCard>
        )}
      </StyledGrid>
      <StyledGrid size={isDesktop ? 4 : 12}>
        {bonusAgreementData ? <BonusAgreementTile {...bonusAgreementData} /> : noData()}
      </StyledGrid>
    </>
  );
}

function getLastUpdatedString(date: string) {
  return dayjs(date, FALLBACK_DATE_FORMAT).format(getPattern(DateFormatType.DATE));
}

const NoDataCard = styled(Card)({
  display: "flex",
  padding: "16px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  height: "336px",
});

const NodataImage = styled("img")({
  height: "164px",
  width: "288px",
});

type CustomerPerformanceBonusAgreementDTO = CustomerPerformanceGraphsData["customerPerformanceBonusAgreement"];

function transformBonusAgreementData(
  dto: CustomerPerformanceBonusAgreementDTO,
  bonusGraphView: CustomerPerformanceGraphsData["bonusGraphView"],
) {
  if (!dto.data) {
    return null;
  }

  return {
    data: dto.data,
    currency: dto.currency,
    date: dto.lastUpdateDate ? new Date(dto.lastUpdateDate) : new Date(),
    initialSelectedPeriod: bonusGraphView,
  };
}

const SpacedCardHeader = styled(CardHeader)({
  height: "80px",
  "& .MuiCardHeader-content": {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
});

const StyledCard = styled(Card)({
  width: "100%",
});

const StyledGrid = styled(Grid)({
  display: "flex",
});
